// Gula de Agua business vocabulary. Values are stored in the database;
// labels are what the team sees (Spanish, the team's working language).

export type Option = { value: string; label: string };

export const CLIENT_STAGES: Option[] = [
  { value: "new", label: "Nuevo" },
  { value: "no_answer", label: "No contesta" },
  { value: "first_contact", label: "1er contacto" },
  { value: "second_contact", label: "2º contacto" },
  { value: "recontact", label: "Recontactar" },
  // Agreed to buy: only the payment / order is missing
  { value: "sure_purchase", label: "Cierre de compra" },
  { value: "no_purchase", label: "No compra" },
  { value: "purchased", label: "Compra" },
  // Out of the setters' board: the admins (or an agent) try to wake them up
  { value: "reactivation", label: "Reactivar" },
];

/**
 * Columns of the sales funnel: every column is the next action. «1er/2º
 * contacto» are gone (4 nov 2026): after talking, the setter always chooses
 * Recontactar (with a date), Cierre de compra, Compra or No compra. Their
 * values stay in CLIENT_STAGES for the labels of the history.
 */
export const LEGACY_STAGES = ["first_contact", "second_contact"];
export const PIPELINE_STAGES: Option[] = CLIENT_STAGES.filter(
  (stage) => !LEGACY_STAGES.includes(stage.value),
);

export const ORDER_STAGES: Option[] = [
  { value: "to_ship", label: "Pendiente de envío" },
  { value: "shipped", label: "Enviado" },
  { value: "received", label: "Recibido" },
  { value: "installer_notified", label: "Instalación solicitada" },
  { value: "installed", label: "Instalado" },
];

export const PRODUCTS: (Option & { price: number })[] = [
  { value: "blua_auxiliary_tap", label: "BLUA grifo auxiliar", price: 499 },
  { value: "blua_three_way_tap", label: "BLUA grifo 3 vías", price: 699 },
];

export const PLANS: Option[] = [
  { value: "none", label: "Sin mantenimiento" },
  { value: "maintenance_monthly", label: "Mantenimiento mensual" },
  { value: "maintenance_annual", label: "Mantenimiento anual" },
];

/** Stripe subscription statuses, as the team reads them. */
export const SUBSCRIPTION_STATUSES: (Option & {
  tone: "ok" | "warn" | "off";
})[] = [
  { value: "active", label: "Activo", tone: "ok" },
  { value: "trialing", label: "En prueba", tone: "ok" },
  { value: "past_due", label: "Impago", tone: "warn" },
  { value: "unpaid", label: "Impagado", tone: "warn" },
  { value: "incomplete", label: "Pago incompleto", tone: "warn" },
  { value: "paused", label: "Pausado", tone: "off" },
  { value: "canceled", label: "Cancelado", tone: "off" },
  { value: "incomplete_expired", label: "Caducado", tone: "off" },
];

/** What a Stripe subscription is for. */
export const SUBSCRIPTION_KINDS: Option[] = [
  { value: "maintenance", label: "Mantenimiento" },
  { value: "rental", label: "Alquiler" },
  { value: "installments", label: "Pago a plazos" },
  { value: "other", label: "Otro" },
];

export const PAYMENT_STATUSES: Option[] = [
  { value: "paid", label: "Pagado" },
  { value: "pending", label: "Pendiente" },
  { value: "refunded", label: "Devuelto" },
];

export const PAYMENT_METHODS: Option[] = [
  { value: "card", label: "Pago completo (tarjeta)" },
  { value: "klarna", label: "Klarna 3 meses" },
  { value: "financing", label: "Financiera (seQura)" },
  { value: "transfer", label: "Transferencia" },
  { value: "cash", label: "Efectivo (solo Cataluña)" },
  { value: "stripe_installments", label: "3 cuotas (Stripe)" },
  { value: "rental", label: "Alquiler" },
];

export const CALL_SLOTS: Option[] = [
  { value: "9-14", label: "9:00 – 14:00" },
  { value: "14-18", label: "14:00 – 18:00" },
  { value: "18-21", label: "18:00 – 21:00" },
];

export const LEAD_TEMPERATURES: (Option & { className: string })[] = [
  { value: "hot", label: "Caliente", className: "bg-red-100 text-red-800" },
  {
    value: "warm",
    label: "Templado",
    className: "bg-amber-100 text-amber-800",
  },
  { value: "cold", label: "Frío", className: "bg-sky-100 text-sky-800" },
];

/** Hot leads first, then warm, cold and unscored. */
export const temperatureRank = (value?: string | null) => {
  const index = LEAD_TEMPERATURES.findIndex((option) => option.value === value);
  return index === -1 ? LEAD_TEMPERATURES.length : index;
};

/**
 * Why a lead does not buy (4 nov 2026). Only real «no» stay in No compra:
 * they already have osmosis, bought it somewhere else, have no coverage, do
 * not want to be contacted or are not customers. The rest (no osmosis yet)
 * go to Reactivar with their reason, to win them back with content for their
 * case. Keep in sync with gula_lost_lead() in the migrations.
 */
export const LOST_FOR_GOOD = [
  "already_has",
  "bought_elsewhere",
  "no_coverage",
  "do_not_contact",
  "other",
];

export const LOST_REASONS: Option[] = [
  // No compra (tienen ósmosis o no son clientes)
  { value: "already_has", label: "Ya tenía ósmosis" },
  { value: "bought_elsewhere", label: "Ha comprado en otro sitio" },
  { value: "no_coverage", label: "Sin cobertura en su zona" },
  { value: "do_not_contact", label: "No quiere que le contactemos" },
  { value: "other", label: "No es cliente (empresa, comercial, prueba…)" },
  // Reactivar (no tienen ósmosis todavía)
  { value: "price", label: "Precio: ahora no puede pagarlo" },
  { value: "prefers_rental", label: "Prefiere alquiler o renting" },
  { value: "decides_other", label: "Lo decide otra persona y dice que no" },
  { value: "not_interested", label: "No le interesa / no lo ve claro" },
  { value: "not_understood", label: "No entiende el producto" },
  { value: "not_installable", label: "Instalación: no cabe, no hay enchufe…" },
  { value: "well_water", label: "Agua de pozo (necesita análisis)" },
  { value: "cooled_off", label: "Estaba interesado y se enfrió" },
  { value: "no_answer", label: "No contesta" },
];

/** Reasons a setter can choose (No contesta has its own button). */
export const LOST_CHOICES = LOST_REASONS.filter(
  (reason) => reason.value !== "no_answer",
);

const labelOfOne = (options: Option[], value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

/** Label of a value; comma separated values (multi-choice answers) are labelled one by one. */
export const labelOf = (options: Option[], value?: string | null) =>
  value
    ? value
        .split(",")
        .map((part) => labelOfOne(options, part.trim()))
        .join(" · ")
    : "";

export const formatEuro = (amount?: number | null) =>
  amount == null
    ? ""
    : amount.toLocaleString("es-ES", { style: "currency", currency: "EUR" });

export const formatLongDate = (date?: string | null) =>
  date
    ? new Date(date).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

export const formatDate = (date?: string | null) =>
  date
    ? new Date(date).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
      })
    : "";

export const daysSince = (date?: string | null) =>
  date
    ? Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 864e5))
    : 0;
